// ClintoBytes Copilot — the bottom-right launcher and chat window (Phase 1
// quotation G2; gap analysis §4.2a). Staff-only: mounted by App.tsx for a
// signed-in user, never on public surfaces or the platform console.
//
// Rules worth knowing before changing this file:
// - It never opens by itself. A dot on the launcher is the only nudge.
// - The ED doctor page always wins the corner: while a page is showing, the
//   launcher hides and an open window minimises.
// - Nothing here changes data except a prepared action the user confirms,
//   which goes through the normal endpoint (bookSlot) with its normal checks.
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { History, Maximize2, Minimize2, Minus, Plus, SendHorizontal, ThumbsDown, ThumbsUp, Trash2, X } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useAiAvailability } from '@/features/ai-assist/useAiAvailability';
import { useMyAlerts } from '@/features/emergency/hooks';
import { bookSlot } from '@/features/appointments/api';
import { openFeedback } from '@/features/feedback/api';
import VoiceMicButton from '@/components/VoiceMicButton';
import { getUiPreferences, patchUiPreferences } from '@/api';
import { cn } from '@/lib/cn';
import {
  OPEN_COPILOT_EVENT, askCopilot, deleteCopilotSession, fetchCopilotSession, fetchCopilotSessions, rateCopilotTurn,
  reportCopilotAction, type CopilotAction, type CopilotLink, type CopilotPart, type CopilotSessionSummary,
} from './api';
import { CopilotParts } from './CopilotParts';

export const COPILOT_BUTTON_PREF = 'hms_copilot_button_hidden';
const EXPANDED_PREF = 'hms_copilot_expanded';
export const COPILOT_BUTTON_EVENT = 'hms:copilot-button';

/** The mascot, full figure, served at the device's pixel density. */
export function CopilotMascot({ size, className }: { size: 24 | 56 | 96; className?: string }) {
  const set = size === 96 ? '/clintobytes-copilot-192.webp 2x, /clintobytes-copilot-288.webp 3x'
    : '/clintobytes-copilot-112.webp 2x, /clintobytes-copilot-168.webp 3x';
  const src = size === 96 ? '/clintobytes-copilot-112.webp' : '/clintobytes-copilot-56.webp';
  const box = size === 96 ? 'h-24 w-24' : size === 56 ? 'h-14 w-14' : 'h-6 w-6';
  return <img src={src} srcSet={set} alt="" draggable={false} className={cn(box, 'select-none rounded-full', className)} />;
}

function readFlag(key: string): boolean {
  try { return localStorage.getItem(key) === '1'; } catch { return false; }
}
/** A per-user on/off preference: localStorage for the instant value, ui_preferences so it follows the user. */
export function useUiFlag(key: string): [boolean, (v: boolean) => void] {
  const [value, setValue] = useState(() => readFlag(key));
  useEffect(() => {
    let cancelled = false;
    getUiPreferences().then((p) => {
      if (cancelled || typeof p[key] !== 'boolean') return;
      setValue(p[key] as boolean);
      try { localStorage.setItem(key, p[key] ? '1' : '0'); } catch { /* the server copy still applies */ }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [key]);
  const set = useCallback((v: boolean) => {
    setValue(v);
    try { localStorage.setItem(key, v ? '1' : '0'); } catch { /* in-memory value still works */ }
    patchUiPreferences({ [key]: v }).catch(() => {});
  }, [key]);
  return [value, set];
}

interface Turn { id: number | null; question: string; parts: CopilotPart[] | null; error?: string; rating?: -1 | 1 | null; actionState: 'prepared' | 'confirmed' | 'cancelled' | 'none' }

function starters(modules: string[]): string[] {
  const has = (m: string) => modules.includes(m);
  const out: string[] = [];
  if (has('queue') || has('visit')) out.push('Who is waiting right now?');
  if (has('appointments')) out.push("Today's appointments");
  if (has('pharmacy')) out.push('What is low on stock?', 'What expires in the next 30 days?');
  if (has('reports') || has('dashboard')) out.push('Visits in the last 30 days, by week');
  if (has('reports')) out.push("Yesterday's collections", 'This month vs last month');
  if (has('ip')) out.push('Who is admitted now?');
  return out.slice(0, 4);
}

export function CopilotLauncher({ onNavigate }: { onNavigate: (link: CopilotLink) => void }) {
  const { user } = useAuth();
  const modules = user?.modules ?? [];
  const { aiEnabled } = useAiAvailability();
  const granted = modules.includes('copilot');
  const alerts = useMyAlerts(granted && modules.includes('emergency') && modules.includes('er:clinical'));
  const paged = (alerts.data?.alerts.length ?? 0) > 0;

  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useUiFlag(EXPANDED_PREF);
  const [buttonHidden, setButtonHidden] = useUiFlag(COPILOT_BUTTON_PREF);
  const [view, setView] = useState<'chat' | 'history'>('chat');
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [unseen, setUnseen] = useState(false);
  const [sessions, setSessions] = useState<CopilotSessionSummary[] | null>(null);
  const openRef = useRef(open); openRef.current = open;
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const available = granted && aiEnabled;

  const ask = useCallback(async (text: string) => {
    const q = text.trim();
    if (!q || busy) return;
    setInput('');
    setView('chat');
    setTurns((t) => [...t, { id: null, question: q, parts: null, actionState: 'none' }]);
    setBusy(true);
    try {
      const r = await askCopilot(q, sessionId);
      setSessionId(r.session_id);
      const hasAction = r.parts.some((p) => p.type === 'action');
      setTurns((t) => t.map((x, i) => (i === t.length - 1 ? { ...x, id: r.turn_id, parts: r.parts, actionState: hasAction ? 'prepared' : 'none' } : x)));
    } catch (e) {
      const msg = (e as Error).message || 'Copilot is unavailable — use the menus as usual.';
      setTurns((t) => t.map((x, i) => (i === t.length - 1 ? { ...x, error: msg } : x)));
    } finally {
      setBusy(false);
      if (!openRef.current) setUnseen(true);
    }
  }, [busy, sessionId]);

  // Opened from Ctrl+K ("Ask Copilot: …"), Alt+/ or the profile menu.
  useEffect(() => {
    const onOpen = (e: Event) => {
      setOpen(true); setUnseen(false);
      const q = (e as CustomEvent<{ question?: string }>).detail?.question;
      if (q) void ask(q);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.altKey && e.key === '/') { e.preventDefault(); setOpen((o) => !o); setUnseen(false); }
    };
    const onButton = (e: Event) => setButtonHidden(!(e as CustomEvent<{ show: boolean }>).detail.show);
    window.addEventListener(OPEN_COPILOT_EVENT, onOpen);
    window.addEventListener(COPILOT_BUTTON_EVENT, onButton);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener(OPEN_COPILOT_EVENT, onOpen);
      window.removeEventListener(COPILOT_BUTTON_EVENT, onButton);
      window.removeEventListener('keydown', onKey);
    };
  }, [ask, setButtonHidden]);

  // The ED page wins the corner.
  useEffect(() => { if (paged) setOpen(false); }, [paged]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' }); }, [turns, busy]);
  useEffect(() => { if (open && view === 'chat') inputRef.current?.focus(); }, [open, view]);

  if (!user || !available) return null;

  const newChat = () => { setSessionId(null); setTurns([]); setView('chat'); };
  const showHistory = async () => {
    setView('history'); setSessions(null);
    try { setSessions(await fetchCopilotSessions()); } catch { setSessions([]); }
  };
  const loadSession = async (id: number) => {
    const s = await fetchCopilotSession(id);
    setSessionId(s.id);
    setTurns(s.turns.map((t) => ({ id: t.id, question: t.question, parts: t.parts, rating: t.rating, actionState: t.action_status as Turn['actionState'] })));
    setView('chat');
  };
  const removeSession = async (id: number) => {
    await deleteCopilotSession(id);
    setSessions((s) => s?.filter((x) => x.id !== id) ?? null);
    if (sessionId === id) newChat();
  };
  const rate = (turn: Turn, rating: 1 | -1) => {
    if (!turn.id) return;
    setTurns((t) => t.map((x) => (x.id === turn.id ? { ...x, rating } : x)));
    rateCopilotTurn(turn.id, rating).catch(() => {});
  };
  const setActionState = (turn: Turn, state: 'confirmed' | 'cancelled') => {
    setTurns((t) => t.map((x) => (x.id === turn.id ? { ...x, actionState: state } : x)));
    if (turn.id) reportCopilotAction(turn.id, state).catch(() => {});
  };
  const handoff = (turn: Turn) => {
    const said = turn.parts?.find((p) => p.type === 'text');
    openFeedback({
      category: 'question',
      message: `Asked ClintoBytes Copilot: ${turn.question}\n\nIt answered: ${said && said.type === 'text' ? said.text : '(no answer)'}`,
    });
  };
  const go = (link: CopilotLink) => { onNavigate(link); if (!expanded) setOpen(false); };
  const submit = (e: FormEvent) => { e.preventDefault(); void ask(input); };

  const firstName = (user.full_name || '').replace(/^dr\.?\s*/i, '').split(/\s+/)[0];

  return (
    <>
      {!open && !buttonHidden && !paged && (
        <button type="button" onClick={() => { setOpen(true); setUnseen(false); }}
          aria-label="Ask ClintoBytes Copilot" title="Ask ClintoBytes Copilot (Alt + /)"
          className="fixed bottom-4 right-4 z-[140] rounded-full p-0 transition-transform hover:scale-105 print:hidden">
          {/* The frame is a speech bubble whose tail points down at the page's bottom edge, so the
              Copilot reads as popping up from the bottom-right corner. */}
          <svg aria-hidden="true" viewBox="0 0 64 70" className="pointer-events-none absolute -left-[2px] -top-[2px] h-[70px] w-16 drop-shadow-lg">
            <path d="M46.6 53.8 L44 68 L32.5 58.9 A29 29 0 1 1 46.6 53.8 Z" className="fill-white stroke-teal/40" strokeWidth={2} strokeLinejoin="round" />
          </svg>
          <CopilotMascot size={56} className="relative" />
          {unseen && <span className="absolute right-0 top-0 h-3 w-3 rounded-full border-2 border-white bg-danger" />}
        </button>
      )}

      {open && (
        <section role="dialog" aria-label="ClintoBytes Copilot"
          onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
          className={cn(
            'fixed z-[160] flex flex-col overflow-hidden border border-border bg-card shadow-2xl print:hidden',
            'inset-0 sm:inset-auto',
            expanded
              ? 'sm:bottom-0 sm:right-0 sm:top-0 sm:w-[440px] sm:border-y-0 sm:border-r-0'
              : 'sm:bottom-2 sm:right-4 sm:h-[600px] sm:max-h-[calc(100vh-1rem)] sm:w-[380px] sm:rounded-xl',
          )}>
          <header className="flex items-center gap-2 border-b border-border px-3 py-2">
            <CopilotMascot size={24} />
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-semibold text-ink">ClintoBytes Copilot</div>
              <div className="truncate text-[11px] text-ink-muted">Answers use your own permissions</div>
            </div>
            <button type="button" className="rounded p-1 text-ink-muted hover:bg-muted" title="New chat" onClick={newChat}><Plus className="h-4 w-4" /></button>
            <button type="button" className="rounded p-1 text-ink-muted hover:bg-muted" title="Earlier conversations" onClick={showHistory}><History className="h-4 w-4" /></button>
            <button type="button" className="hidden rounded p-1 text-ink-muted hover:bg-muted sm:block" title={expanded ? 'Float' : 'Expand'} onClick={() => setExpanded(!expanded)}>
              {expanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
            <button type="button" className="rounded p-1 text-ink-muted hover:bg-muted" title="Minimise" onClick={() => setOpen(false)}><Minus className="h-4 w-4" /></button>
            <button type="button" className="rounded p-1 text-ink-muted hover:bg-muted" title="Close" onClick={() => { setOpen(false); newChat(); }}><X className="h-4 w-4" /></button>
          </header>

          {view === 'history' ? (
            <div className="flex-1 overflow-y-auto p-3">
              <div className="mb-2 text-[12px] font-semibold text-ink">Earlier conversations</div>
              {sessions === null && <div className="text-[12px] text-ink-muted">Loading…</div>}
              {sessions?.length === 0 && <div className="text-[12px] text-ink-muted">No earlier conversations. They are kept for 30 days.</div>}
              {sessions?.map((s) => (
                <div key={s.id} className="flex items-center gap-2 border-b border-border py-1.5">
                  <button type="button" className="min-w-0 flex-1 text-left" onClick={() => void loadSession(s.id)}>
                    <div className="truncate text-[13px] text-ink">{s.title}</div>
                    <div className="text-[11px] text-ink-muted">{new Date(s.last_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })} · {s.turns} question{s.turns === 1 ? '' : 's'}</div>
                  </button>
                  <button type="button" className="rounded p-1 text-ink-muted hover:text-danger" title="Delete" onClick={() => void removeSession(s.id)}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              <button type="button" className="mt-3 text-[12px] font-medium text-teal hover:underline" onClick={() => setView('chat')}>← Back to chat</button>
            </div>
          ) : (
            <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto p-3">
              {!turns.length && (
                <div className="flex flex-col items-center pt-4 text-center">
                  <CopilotMascot size={96} />
                  <div className="mt-2 text-[14px] font-semibold text-ink">Hi {firstName} — how can I help?</div>
                  <div className="mt-1 text-[12px] text-ink-muted">Ask in English, Tamil or Thanglish. I can find information, explain how to do things and open the right screen.</div>
                  <div className="mt-3 flex flex-wrap justify-center gap-1.5">
                    {starters(modules).map((s) => (
                      <button key={s} type="button" onClick={() => void ask(s)}
                        className="rounded-full border border-border bg-card px-3 py-1 text-[12px] text-ink hover:border-teal">{s}</button>
                    ))}
                    <button type="button" onClick={() => { setInput('How do I '); inputRef.current?.focus(); }}
                      className="rounded-full border border-border bg-card px-3 py-1 text-[12px] text-ink hover:border-teal">How do I…?</button>
                  </div>
                </div>
              )}
              {turns.map((t, i) => (
                <div key={t.id ?? `p${i}`} className="space-y-2">
                  <div className="ml-auto w-fit max-w-[85%] whitespace-pre-wrap rounded-lg rounded-br-sm bg-teal px-3 py-1.5 text-[13px] text-white">{t.question}</div>
                  <div className="flex gap-2">
                    <CopilotMascot size={24} className="mt-0.5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      {!t.parts && !t.error && <div className="text-[13px] text-ink-muted">Thinking…</div>}
                      {t.error && <div className="text-[13px] text-danger">{t.error}</div>}
                      {t.parts && (
                        <CopilotParts parts={t.parts} h={{
                          onNavigate: go,
                          onAsk: (q) => void ask(q),
                          onHandoff: () => handoff(t),
                          actionState: t.actionState,
                          onConfirmAction: async (a: CopilotAction) => {
                            await bookSlot(a.slot_id, { patient_id: a.patient_id });
                            setActionState(t, 'confirmed');
                          },
                          onCancelAction: () => setActionState(t, 'cancelled'),
                        }} />
                      )}
                      {t.parts && t.id && (
                        <div className="mt-1 flex gap-1 text-ink-muted">
                          <button type="button" title="Helpful" onClick={() => rate(t, 1)} className={cn('rounded p-0.5 hover:text-ink', t.rating === 1 && 'text-success')}><ThumbsUp className="h-3.5 w-3.5" /></button>
                          <button type="button" title="Not helpful" onClick={() => rate(t, -1)} className={cn('rounded p-0.5 hover:text-ink', t.rating === -1 && 'text-danger')}><ThumbsDown className="h-3.5 w-3.5" /></button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <form onSubmit={submit} className="flex items-end gap-1.5 border-t border-border p-2">
            <VoiceMicButton title="Speak your question" translateToEnglish={false} onText={(txt) => setInput((v) => (v ? `${v} ${txt}` : txt))} />
            <textarea ref={inputRef} rows={1} value={input} placeholder="Ask anything…" maxLength={2000}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void ask(input); } }}
              className="max-h-28 min-h-9 flex-1 resize-none rounded-md border border-border bg-card px-2.5 py-1.5 text-[13px] text-ink" />
            <button type="submit" disabled={busy || !input.trim()} title="Send"
              className="flex h-9 w-9 items-center justify-center rounded-md bg-teal text-white disabled:opacity-40"><SendHorizontal className="h-4 w-4" /></button>
          </form>
        </section>
      )}
    </>
  );
}
